import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Brain, Smartphone, Zap, Shield, TrendingUp } from 'lucide-react';

const Solution = () => {
  const solutions = [
    {
      icon: Camera,
      title: "Advanced UV Imaging Technology",
      description: "Our proprietary 405nm UV LED technology illuminates hidden plaque and dental issues invisible to the naked eye. Custom hardware provides clinical-grade precision in a mobile form factor.",
      features: ["405nm UV illumination", "Clinical-grade precision", "Mobile-optimized design", "Real-time capture"],
      color: "bg-gradient-to-br from-blue-500 to-blue-600",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50"
    },
    {
      icon: Brain,
      title: "AI-Powered Detection Suite",
      description: "State-of-the-art YOLOv11 + ResNet50 models trained on extensive clinical data detect plaque, gingivitis, and calculus with exceptional accuracy across diverse patient populations.",
      features: ["YOLOv11 segmentation", "ResNet50 classification", "Clinical validation", "95% accuracy"],
      color: "bg-gradient-to-br from-purple-500 to-purple-600",
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50"
    },
    {
      icon: Smartphone,
      title: "Intuitive Mobile Experience",
      description: "Built with Flutter and Firebase, our app delivers instant results, personalized insights, and actionable oral care recommendations for continuous health monitoring.",
      features: ["Flutter development", "Firebase backend", "Real-time results", "Personalized insights"],
      color: "bg-gradient-to-br from-green-500 to-green-600",
      borderColor: "border-green-200",
      bgColor: "bg-green-50"
    }
  ];

  const performanceMetrics = [
    {
      metric: "90%",
      label: "Plaque Detection (mAP)",
      icon: Shield,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      metric: "75%",
      label: "Gingivitis Detection (mAP)",
      icon: TrendingUp,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      metric: "99.5%",
      label: "Area of Interest (mAP)",
      icon: Zap,
      color: "text-green-600",
      bgColor: "bg-green-50"
    }
  ];

  return (
    <section id="solution" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center px-4 py-2 bg-oralytics-50 border border-oralytics-200 rounded-full text-sm font-medium text-oralytics-700 mb-6">
            <Zap className="w-4 h-4 mr-2" />
            Revolutionary Technology
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            The <span className="text-gradient">Oralytics</span> Solution
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            A complete hardware and software ecosystem that brings professional dental analysis 
            to your smartphone with clinical-grade accuracy and ease of use.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`bg-white rounded-2xl p-8 shadow-xl card-hover border ${solution.borderColor}`}
            >
              <div className={`w-20 h-20 ${solution.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                <solution.icon className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {solution.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                {solution.description}
              </p>

              <div className="space-y-3">
                {solution.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-3">
                    <div className={`w-2 h-2 ${solution.color} rounded-full`}></div>
                    <span className="text-sm text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Detection & Performance Section - Two Horizontal Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Card: Table Image with Heading/Description */}
            <div className="flex-1 bg-white rounded-2xl p-8 shadow-xl border border-gray-200 flex flex-col items-center justify-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Detection Technology in Action</h3>
              <p className="text-gray-600 text-base mb-6 text-center max-w-lg">
                See how our UV imaging reveals plaque invisible to the naked eye and the clinical-grade accuracy of our AI models.
              </p>
              <img 
                src="/images/technology/teeth-comparison-table.png" 
                alt="Teeth Comparison: Natural, UVA, Masked, and Detected Views" 
                className="max-w-xl w-full h-auto rounded-xl shadow-lg object-contain mb-2"
              />
            </div>
            {/* Right Card: Clinical Performance Stats */}
            <div className="flex-1 bg-white rounded-2xl p-8 shadow-xl border border-gray-200 flex flex-col items-center justify-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Clinical Performance</h3>
              <p className="text-gray-600 text-base mb-6 text-center max-w-md">
                Our AI models achieve clinical-grade accuracy across diverse patient populations.
              </p>
              <div className="flex flex-col w-full max-w-xs divide-y divide-gray-200">
                <div className="flex flex-col py-4">
                  <div className="flex items-center">
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg mr-4">
                      {/* Shield Icon */}
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 3l7 4v5c0 5.25-3.5 9.74-7 11-3.5-1.26-7-5.75-7-11V7l7-4z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">90%</div>
                      <div className="text-sm text-gray-700">Plaque Detection (mAP)</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 ml-14">Validated on 1,200+ clinical images</div>
                </div>
                <div className="flex flex-col py-4">
                  <div className="flex items-center">
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-purple-100 rounded-lg mr-4">
                      {/* Trending Up Icon */}
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 17l6-6 4 4 8-8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">75%</div>
                      <div className="text-sm text-gray-700">Gingivitis Detection (mAP)</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 ml-14">Benchmarked against expert dentists</div>
                </div>
                <div className="flex flex-col py-4">
                  <div className="flex items-center">
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                      {/* Zap Icon */}
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8L21 10h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <div>
                      <div className="text-2xl font-bold text-green-600">99.5%</div>
                      <div className="text-sm text-gray-700">Area of Interest (mAP)</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 ml-14">Covers all major oral health risks</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Solution; 